import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ArrowRight,
  Phone,
  MapPin,
  Clock,
  Hospital,
  Briefcase,
  Droplet,
  HeartPulse,
  Repeat,
  Users,
  Building2,
  AlertTriangle,
  Accessibility,
  Home as HomeIcon,
} from 'lucide-react';

const CANONICAL_PATH = '/richmond-tx-medical-transportation';

const RICHMOND_FAQS = [
  {
    question: 'Is Richmond, TX part of MoveraCare’s core service area?',
    answer:
      'Yes. Richmond is the county seat of Fort Bend County, which is MoveraCare Mobility’s primary operating area. Richmond neighborhoods sit at the center of our regular non-emergency medical transportation coverage.',
  },
  {
    question: 'What Richmond neighborhoods do you serve?',
    answer:
      'MoveraCare serves Richmond broadly, including Aliana, Long Meadow Farms, Pecan Grove, Waterside Estates, Del Webb Sweetgrass, Bonbrook Plantation, Lakes of Bella Terra, Grand Mission, Winston Ranch, Canyon Gate at the Brazos, and the historic downtown Richmond area, along with adjacent Rosenberg neighborhoods when trips originate or terminate in Richmond. Service is scheduled subject to availability.',
  },
  {
    question: 'Do you transport patients from Richmond to Sugar Land, Missouri City, Katy, or the Texas Medical Center?',
    answer:
      'Yes. Trips between Richmond and other Fort Bend / Greater Houston destinations are common — Sugar Land, Missouri City, Katy, Houston, and the Texas Medical Center among them. Same-county trips are our routine coverage; longer runs are supported subject to scheduling.',
  },
  {
    question: 'Do you offer recurring dialysis transportation in Richmond?',
    answer:
      'Yes. Standing weekly schedules are supported for Richmond dialysis patients — Monday/Wednesday/Friday, Tuesday/Thursday/Saturday, or other recurring plans coordinated with the treating dialysis center. Wait-and-return can be arranged in advance.',
  },
  {
    question: 'Can Richmond hospitals and SNFs request discharge transportation?',
    answer:
      'Yes. Richmond and Fort Bend County discharge planners, case managers, and social workers can request non-emergency discharge transportation from a hospital or skilled nursing facility to home, rehabilitation, or assisted living. Same-day and scheduled discharges are supported for medically stable patients based on availability.',
  },
  {
    question: 'How do I book a wheelchair-accessible ride in Richmond?',
    answer:
      'Call dispatch at (832) 925-2128 or use the online Book a Ride form. Family members, case managers, or facility staff arranging a Richmond ride on behalf of a patient can use the Request Patient Transportation form.',
  },
  {
    question: "Do you accept Workers' Compensation authorizations for Richmond-area injured workers?",
    answer:
      "Yes. MoveraCare accepts authorized non-emergency transportation for Richmond-area injured workers traveling to Workers' Compensation-approved medical, therapy, IME, FCE, and occupational medicine appointments. Authorization is determined by the applicable adjuster, case manager, TPA, or payer.",
  },
  {
    question: 'How far in advance should Richmond riders schedule?',
    answer:
      'For routine Richmond trips we recommend 24–48 hours notice. Recurring dialysis, therapy, and rehabilitation runs should be established as a standing schedule. Same-day requests may be considered when vehicle and driver availability allows — we do not guarantee same-day acceptance.',
  },
];

const RICHMOND_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': ['MedicalBusiness', 'LocalBusiness'],
  '@id': 'https://www.moveracaremobility.com/richmond-tx-medical-transportation#business',
  name: 'MoveraCare Mobility — Richmond, TX (Fort Bend County) Non-Emergency Medical Transportation',
  legalName: 'MoveraCare Services LLC',
  description:
    "MoveraCare Mobility's Richmond, TX hub — the operating base for wheelchair and ambulatory non-emergency medical transportation across Fort Bend County, Harris County, and Greater Houston, including Sugar Land, Missouri City, Katy, Rosenberg, Stafford, and Fulshear. Services include medical appointments, dialysis, hospital discharge, rehabilitation, and Workers' Compensation.",
  url: 'https://www.moveracaremobility.com/richmond-tx-medical-transportation',
  telephone: '+1-832-925-2128',
  email: 'dispatch@moveracaremobility.com',
  priceRange: '$$',
  areaServed: [
    { '@type': 'City', name: 'Richmond, TX' },
    { '@type': 'City', name: 'Sugar Land, TX' },
    { '@type': 'City', name: 'Missouri City, TX' },
    { '@type': 'City', name: 'Katy, TX' },
    { '@type': 'City', name: 'Rosenberg, TX' },
    { '@type': 'City', name: 'Stafford, TX' },
    { '@type': 'City', name: 'Fulshear, TX' },
    { '@type': 'AdministrativeArea', name: 'Fort Bend County, Texas' },
    { '@type': 'AdministrativeArea', name: 'Harris County, Texas' },
  ],
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Richmond',
    addressRegion: 'TX',
    addressCountry: 'US',
  },
  openingHoursSpecification: [
    { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'], opens: '06:00', closes: '19:00' },
    { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Sunday'], opens: '12:00', closes: '17:00' },
  ],
};

const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: RICHMOND_FAQS.map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: { '@type': 'Answer', text: f.answer },
  })),
};

const SERVICE_TYPES = [
  { icon: Accessibility, title: 'Wheelchair Transportation', desc: 'Wheelchair-accessible transportation for Richmond patients across Fort Bend County.', to: '/services/wheelchair-transportation' },
  { icon: Users, title: 'Ambulatory Transportation', desc: 'Comfortable ambulatory transport for medically stable Richmond patients.', to: '/services/ambulatory-transportation' },
  { icon: Hospital, title: 'Hospital Discharge Transportation', desc: 'Same-day and scheduled discharge transportation from Fort Bend County hospitals and SNFs.', to: '/services/hospital-discharge' },
  { icon: Droplet, title: 'Dialysis Transportation', desc: 'Recurring standing schedules for Richmond dialysis patients.', to: '/services/dialysis-transportation' },
  { icon: HeartPulse, title: 'Therapy & Rehabilitation Transportation', desc: 'Physical therapy, occupational therapy, and rehabilitation appointment transportation.', to: '/services/therapy-rehabilitation-transportation' },
  { icon: Briefcase, title: "Workers' Compensation Transportation", desc: 'Authorized transportation for Richmond-area injured workers.', to: '/healthcare-partners/workers-compensation' },
  { icon: Repeat, title: 'Recurring Medical Transportation', desc: 'Standing NEMT schedules for Richmond patients with ongoing treatment plans.', to: '/services/recurring-medical-transportation' },
  { icon: Clock, title: 'Wait-and-Return Transportation', desc: 'Driver-waiting service for shorter Richmond appointments when arranged in advance.', to: '/services' },
];

const RICHMOND_AREAS = [
  'Historic Downtown Richmond',
  'Aliana',
  'Long Meadow Farms',
  'Pecan Grove',
  'Waterside Estates',
  'Del Webb Sweetgrass',
  'Bonbrook Plantation',
  'Lakes of Bella Terra',
  'Grand Mission',
  'Winston Ranch',
  'Canyon Gate at the Brazos',
  'Rio Vista',
  'Briscoe Falls',
  'FM 359 / FM 762 corridor',
  'Highway 90A corridor',
  'Grand Parkway (99) corridor',
];

const NEARBY_LINKS = [
  { name: 'Sugar Land', to: '/sugar-land-tx-medical-transportation', note: 'Frequent Richmond → Sugar Land medical trips.' },
  { name: 'Katy', to: '/katy-tx-medical-transportation', note: 'Cross-county coverage into Fort Bend/Harris/Waller.' },
  { name: 'Missouri City', to: '/missouri-city-tx-medical-transportation', note: 'Adjacent Fort Bend County city coverage.' },
  { name: 'Houston', to: '/houston-tx-medical-transportation', note: 'Trips into Houston and the Texas Medical Center.' },
];

const RichmondMedicalTransportation = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="richmond-page">
      <Seo
        title="Non-Emergency Medical Transportation Richmond TX | MoveraCare Mobility"
        description="MoveraCare Mobility is a Richmond, TX-based non-emergency medical transportation provider serving Fort Bend County — wheelchair and ambulatory NEMT for medical, dialysis, discharge, therapy, and Workers' Compensation trips. Call 832-925-2128."
        path={CANONICAL_PATH}
        schema={[RICHMOND_SCHEMA, FAQ_SCHEMA]}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Service Areas', url: '/services' },
          { name: 'Richmond, TX', url: CANONICAL_PATH },
        ]}
      />

      {/* Hero */}
      <section className="py-16 md:py-20 bg-[#004C97]" data-testid="richmond-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="text-sm text-white/80 mb-6" data-testid="breadcrumbs">
            <ol className="flex flex-wrap items-center gap-1">
              <li><Link to="/" className="hover:text-[#F9A825]">Home</Link></li>
              <li className="text-white/50">/</li>
              <li><Link to="/services" className="hover:text-[#F9A825]">Service Areas</Link></li>
              <li className="text-white/50">/</li>
              <li className="text-[#F9A825] font-semibold">Richmond, TX</li>
            </ol>
          </nav>
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-[#F9A825] text-[#004C97] px-4 py-1.5 rounded-full text-sm font-bold mb-6">
              <HomeIcon className="w-4 h-4" />
              OUR HOME CITY · FORT BEND COUNTY HUB
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Non-Emergency Medical Transportation in Richmond, Texas
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Richmond is MoveraCare Mobility&apos;s home city and the center of our Fort Bend County service area. We provide professional wheelchair and ambulatory non-emergency medical transportation for Richmond patients, families, healthcare facilities, case managers, and Workers&apos; Compensation coordinators.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              <Link to="/book">
                <Button size="lg" className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold text-lg px-8 shadow-lg" data-testid="richmond-cta-book">
                  Book a Ride
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/request-patient-transportation">
                <Button size="lg" className="bg-white text-[#004C97] hover:bg-gray-100 font-semibold text-lg px-8 shadow-lg" data-testid="richmond-cta-refer">
                  Refer a Patient
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:+18329252128">
                <Button size="lg" variant="outline" className="border-2 border-[#F9A825] text-[#F9A825] hover:bg-[#F9A825] hover:text-[#004C97] font-semibold text-lg px-8" data-testid="richmond-cta-call">
                  <Phone className="mr-2 w-5 h-5" />
                  Call Dispatch (832) 925-2128
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Overview — Fort Bend Hub */}
      <section className="py-14 bg-white" data-testid="richmond-overview">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">RICHMOND NEMT OVERVIEW</p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-6 leading-tight">
            Richmond, TX — MoveraCare&apos;s Fort Bend County Home Base
          </h2>
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
            <p>
              Richmond is more than a stop on the map for MoveraCare Mobility — it&apos;s where our dispatch, operations, and everyday coordination live. As the county seat of Fort Bend County, Richmond sits at the crossroads of Highway 90A, FM 359, FM 762, and Grand Parkway (99), giving our team direct routing into the surrounding communities where Richmond patients travel most often for medical care.
            </p>
            <p>
              Richmond has grown steadily over the past two decades, with newer master-planned communities (Aliana, Long Meadow Farms, Del Webb Sweetgrass, Bonbrook Plantation, Waterside Estates) sitting alongside the historic downtown grid. That growth has been paired with a rising number of seniors aging in place, patients on standing dialysis or therapy schedules, families arranging discharge coordination, and injured workers on authorized Workers&apos; Compensation care plans. Reliable non-emergency medical transportation is essential daily infrastructure for all of them.
            </p>
            <p>
              MoveraCare&apos;s Richmond services include wheelchair transportation using wheelchair-accessible vehicles, comfortable ambulatory transport, recurring dialysis and therapy runs, hospital and SNF discharge coordination, and authorized Workers&apos; Compensation transportation. All services are provided for medically stable passengers and are scheduled subject to vehicle and driver availability. Extended-distance assignments may require advance authorization.
            </p>
            <p>
              MoveraCare is a non-emergency medical transportation provider. We do not provide ambulance, 911, stretcher ambulance, oxygen therapy, or clinical monitoring services. For medical emergencies, call 911.
            </p>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-14 bg-gray-50" data-testid="richmond-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">SERVICES OFFERED IN RICHMOND</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] leading-tight">
              Richmond Non-Emergency Medical Transportation Services
            </h2>
            <p className="text-gray-700 mt-3 max-w-3xl">
              Every core MoveraCare service is available for Richmond passengers. Explore the details of each service, or visit the full <Link to="/services" className="text-[#F9A825] font-semibold hover:underline">Services</Link> page.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICE_TYPES.map((s) => {
              const Icon = s.icon;
              return (
                <Link key={s.title} to={s.to} className="group" data-testid={`richmond-service-${s.title.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}>
                  <Card className="h-full border-2 hover:border-[#F9A825] transition-all">
                    <CardHeader>
                      <div className="w-11 h-11 rounded-lg bg-[#F9A825] flex items-center justify-center mb-2">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <CardTitle className="text-[#004C97] text-base leading-tight">{s.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 leading-relaxed mb-2">{s.desc}</p>
                      <span className="text-sm font-semibold text-[#F9A825] group-hover:text-[#E08E1E]">Explore →</span>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Wheelchair & Ambulatory (Richmond-specific angle) */}
      <section className="py-14 bg-white" data-testid="richmond-wheelchair-ambulatory">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">WHEELCHAIR TRANSPORTATION</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#004C97] mb-4 leading-tight">
                Richmond Wheelchair-Accessible Transportation
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Richmond&apos;s active-adult and senior-living communities — Del Webb Sweetgrass, several assisted-living properties, and long-standing family neighborhoods in the historic core — generate consistent demand for wheelchair-accessible non-emergency transportation. MoveraCare operates wheelchair-accessible vehicles and follows standard wheelchair securement procedures. Drivers assist passengers during transport. Door-to-door or curb-to-curb assistance is coordinated at booking based on the passenger&apos;s needs and pickup location; please share the wheelchair type, weight, and any equipment needs (including oxygen) when booking so dispatch can confirm we can safely serve the trip.
              </p>
              <Link to="/services/wheelchair-transportation" className="inline-flex items-center text-sm font-semibold text-[#F9A825] hover:text-[#E08E1E]">
                Learn about Wheelchair Transportation <ArrowRight className="ml-1 w-4 h-4" />
              </Link>
            </div>
            <div>
              <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">AMBULATORY TRANSPORTATION</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#004C97] mb-4 leading-tight">
                Richmond Ambulatory Medical Transportation
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                For Richmond patients who can walk to and from the vehicle independently or with limited assistance, MoveraCare provides comfortable, professionally driven ambulatory transportation. Common trips include primary-care visits, cardiology follow-ups, orthopedic appointments, post-surgical checks, diagnostic imaging, and specialty visits in Sugar Land, Missouri City, Katy, and the Texas Medical Center.
              </p>
              <Link to="/services/ambulatory-transportation" className="inline-flex items-center text-sm font-semibold text-[#F9A825] hover:text-[#E08E1E]">
                Learn about Ambulatory Transportation <ArrowRight className="ml-1 w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Fort Bend Hub — nearby city links */}
      <section className="py-14 bg-gray-50" data-testid="richmond-hub">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">FORT BEND COUNTY HUB</p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-4 leading-tight">
            Richmond Anchors Our Fort Bend County Coverage
          </h2>
          <p className="text-gray-700 mb-8 max-w-3xl">
            Because MoveraCare operates out of Richmond, the surrounding Fort Bend County cities are part of our regular coverage. Explore location-specific pages for each neighboring service area:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {NEARBY_LINKS.map((n) => (
              <Link key={n.name} to={n.to} className="group" data-testid={`richmond-nearby-${n.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <Card className="h-full border-2 hover:border-[#F9A825] transition-all">
                  <CardHeader>
                    <div className="w-10 h-10 rounded-lg bg-[#F9A825] flex items-center justify-center mb-2">
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                    <CardTitle className="text-[#004C97] text-base leading-tight">{n.name}, TX</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 leading-relaxed mb-2">{n.note}</p>
                    <span className="text-sm font-semibold text-[#F9A825] group-hover:text-[#E08E1E]">View page →</span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* For Richmond Healthcare Organizations */}
      <section className="py-14 bg-[#004C97]" data-testid="richmond-healthcare-org">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">FOR RICHMOND HEALTHCARE ORGANIZATIONS</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
              Transportation for Fort Bend County Hospitals, SNFs, Dialysis Centers &amp; Case Managers
            </h2>
            <p className="text-gray-200 mt-3 max-w-3xl">
              MoveraCare works directly with Richmond and Fort Bend County hospitals, skilled nursing facilities, rehabilitation centers, dialysis centers, case managers, discharge planners, and social workers. Visit <Link to="/healthcare-partners" className="text-[#F9A825] font-semibold hover:underline">Healthcare Partners</Link> for the full program list.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5">
              <h3 className="text-white font-semibold text-lg mb-2">Discharge coordination</h3>
              <p className="text-gray-200 text-sm leading-relaxed">Same-day and scheduled discharge transportation from Richmond and Fort Bend County hospitals and SNFs to home, rehabilitation, or assisted living.</p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5">
              <h3 className="text-white font-semibold text-lg mb-2">Recurring dialysis transportation</h3>
              <p className="text-gray-200 text-sm leading-relaxed">Weekly standing schedules coordinated with dialysis centers serving Richmond and surrounding Fort Bend communities.</p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5">
              <h3 className="text-white font-semibold text-lg mb-2">Therapy &amp; rehabilitation runs</h3>
              <p className="text-gray-200 text-sm leading-relaxed">Physical therapy, occupational therapy, and rehabilitation appointment transportation across Fort Bend County.</p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5">
              <h3 className="text-white font-semibold text-lg mb-2">Case-manager coordination</h3>
              <p className="text-gray-200 text-sm leading-relaxed">Direct dispatch contact for Richmond-area case managers and coordinators arranging authorized non-emergency transportation.</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Link to="/healthcare-partners">
              <Button size="lg" className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold" data-testid="richmond-cta-healthcare-partners">Healthcare Partner Programs<ArrowRight className="ml-2 w-5 h-5" /></Button>
            </Link>
            <Link to="/healthcare-partners/hospital-discharge">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#004C97] font-semibold">Hospital Discharge</Button>
            </Link>
            <Link to="/healthcare-partners/dialysis-centers">
              <Button size="lg" variant="outline" className="border-2 border-[#F9A825] text-[#F9A825] hover:bg-[#F9A825] hover:text-[#004C97] font-semibold">Dialysis Centers</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Workers' Comp */}
      <section className="py-14 bg-white" data-testid="richmond-workers-comp">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            <div>
              <div className="w-14 h-14 rounded-lg bg-[#F9A825] flex items-center justify-center mb-3">
                <Briefcase className="w-7 h-7 text-white" />
              </div>
              <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">WORKERS&apos; COMPENSATION</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#004C97] leading-tight">
                Richmond Workers&apos; Compensation Transportation
              </h2>
            </div>
            <div className="lg:col-span-2">
              <p className="text-gray-700 leading-relaxed mb-4">
                MoveraCare provides non-emergency transportation for Richmond-area injured workers attending Workers&apos; Compensation-authorized medical, therapy, IME, FCE, occupational medicine, and diagnostic appointments — including trips into Sugar Land, Missouri City, Katy, or Houston where the authorized provider is located. We coordinate with adjusters, nurse case managers, TPAs, and ancillary service coordinators. Authorization determinations are made by the applicable payer, adjuster, case manager, or TPA — being a network vendor does not automatically guarantee that a trip is covered or payable.
              </p>
              <Link to="/healthcare-partners/workers-compensation" className="inline-flex items-center text-sm font-semibold text-[#F9A825] hover:text-[#E08E1E]" data-testid="richmond-cta-workers-comp">
                Workers&apos; Compensation Transportation Details <ArrowRight className="ml-1 w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Served */}
      <section className="py-14 bg-gray-50" data-testid="richmond-areas-served">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">AREAS SERVED</p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-4 leading-tight">
            Richmond Neighborhoods &amp; Fort Bend County Coverage
          </h2>
          <p className="text-gray-700 mb-6 max-w-3xl">
            Richmond neighborhoods, corridors, and adjacent communities within our regular Fort Bend County service area. Service is scheduled subject to availability. Extended-distance assignments may require advance authorization.
          </p>
          <div className="flex flex-wrap gap-2">
            {RICHMOND_AREAS.map((a) => (
              <span key={a} className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm text-[#004C97] font-medium">
                {a}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose */}
      <section className="py-14 bg-white" data-testid="richmond-why">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">WHY CHOOSE MOVERACARE</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97]">Built in Richmond, for Richmond</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: HomeIcon, title: 'Richmond-Based Operations', desc: 'MoveraCare is headquartered in Richmond — this is where our dispatch and drivers work every day.' },
              { icon: Accessibility, title: 'Wheelchair-Accessible Vehicles', desc: 'Wheelchair-accessible vehicles with proper wheelchair securement. Drivers assist passengers during transport.' },
              { icon: Users, title: 'Driver Assistance', desc: 'Drivers assist passengers during transport. Mobility and equipment needs are confirmed at booking.' },
              { icon: Repeat, title: 'Standing Schedules', desc: 'Reliable recurring transportation for Richmond dialysis, therapy, and treatment programs.' },
              { icon: Hospital, title: 'Discharge-Ready Dispatch', desc: 'Responsive same-day and scheduled discharge coordination for Fort Bend County hospitals and SNFs.' },
              { icon: Building2, title: 'Credentialing-Ready', desc: 'Licensing, insurance, and vendor documentation available for Richmond facility procurement teams.' },
            ].map((w) => {
              const Icon = w.icon;
              return (
                <div key={w.title} className="flex items-start gap-3 p-5 border-2 border-gray-100 rounded-xl hover:border-[#F9A825] transition-all">
                  <div className="w-10 h-10 rounded-lg bg-[#F9A825]/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-[#F9A825]" />
                  </div>
                  <div>
                    <h3 className="text-[#004C97] font-semibold mb-1 leading-tight">{w.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{w.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 bg-gray-50" data-testid="richmond-faq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">RICHMOND FAQ</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97]">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {RICHMOND_FAQS.map((f) => (
              <details key={f.question} className="bg-white border-2 border-gray-100 rounded-lg p-5 group">
                <summary className="font-semibold text-[#004C97] cursor-pointer flex items-center justify-between">
                  <span>{f.question}</span>
                  <span className="text-[#F9A825] transition-transform group-open:rotate-90">→</span>
                </summary>
                <p className="mt-3 text-sm text-gray-700 leading-relaxed">{f.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 bg-[#F9A825]" data-testid="richmond-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-4">Request Richmond Medical Transportation</h2>
          <p className="text-lg text-[#004C97]/85 mb-8">Book a Richmond wheelchair or ambulatory ride, refer a patient, or reach dispatch directly.</p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-center">
            <Link to="/book">
              <Button size="lg" className="bg-[#004C97] text-white hover:bg-[#003D7A] font-semibold text-lg px-8 py-6 shadow-lg" data-testid="richmond-final-book">Book a Ride<ArrowRight className="ml-2 w-5 h-5" /></Button>
            </Link>
            <Link to="/request-patient-transportation">
              <Button size="lg" className="bg-white text-[#004C97] hover:bg-gray-100 font-semibold text-lg px-8 py-6 shadow-lg" data-testid="richmond-final-refer">Refer a Patient<ArrowRight className="ml-2 w-5 h-5" /></Button>
            </Link>
            <a href="tel:+18329252128">
              <Button size="lg" variant="outline" className="border-2 border-[#004C97] text-[#004C97] hover:bg-[#004C97] hover:text-white font-semibold text-lg px-8 py-6" data-testid="richmond-final-call"><Phone className="mr-2 w-5 h-5" />(832) 925-2128</Button>
            </a>
          </div>
        </div>
      </section>

      {/* Non-emergency disclaimer */}
      <section className="py-6 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-100 border-l-4 border-gray-400 rounded p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-700 leading-relaxed">
              MoveraCare provides <strong>non-emergency medical transportation</strong> for medically stable passengers in Richmond and Fort Bend County. We do not provide ambulance, 911, emergency response, stretcher ambulance, oxygen therapy, or clinical monitoring services. <strong>Not for medical emergencies. Call 911 in an emergency.</strong>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RichmondMedicalTransportation;
